import firebase from 'firebase/app';
import 'firebase/firestore';
import Bugsnag from '@bugsnag/js';
import _isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { useUserIsAuthenticated } from '../../../app/hooks/useUserIsAuthenticated';
import { Collections, SubCollections } from '../collections';
import { formatDocument } from '../documentConverter';
import { Criteria } from '../query/criteria';

const getFirestoreQuery = (
  userIsAuthenticated: boolean,
  requireAuthentication: boolean,
  collection: Collections,
  documentId: string,
  subCollection: SubCollections,
  queryObjects: Criteria[] | null,
): firebase.firestore.Query | undefined => {
  if (!queryObjects || _isEmpty(documentId)) {
    return undefined;
  }
  if (requireAuthentication && !userIsAuthenticated) {
    return undefined;
  }
  let query: firebase.firestore.Query = firebase.firestore().collection(collection).doc(documentId).collection(subCollection);
  queryObjects.forEach((queryObject: Criteria) => {
    if (Criteria.isWhereCriteria(queryObject)) {
      query = query.where(queryObject.getKey(), queryObject.getOperation(), queryObject.getValue());
    }
    if (Criteria.isLimitCriteria(queryObject)) {
      query = query.limit(queryObject.getLimit());
    }
    if (Criteria.isOrderCriteria(queryObject)) {
      query = query.orderBy(queryObject.getFieldPath(), queryObject.getDirection());
    }
  });
  return query;
};

export const useSubCollectionQuerySnapshot = <T extends firebase.firestore.DocumentData>(
  collection: Collections,
  documentId: string,
  subCollection: SubCollections,
  queryObjects: Criteria[] | null,
  requireAuthentication = true,
): T[] | undefined => {
  const userIsAuthenticated = useUserIsAuthenticated();

  const [querySnapshot, loading, error] = useCollection(
    getFirestoreQuery(userIsAuthenticated, requireAuthentication, collection, documentId, subCollection, queryObjects),
    { snapshotListenOptions: { includeMetadataChanges: true } },
  );

  useEffect(() => {
    if (error) {
      Bugsnag.notify(error, (event) => {
        // eslint-disable-next-line no-param-reassign
        event.context = `Firestore useSubCollectionQuerySnapshot Error ${collection} ${subCollection}`;
        // eslint-disable-next-line no-param-reassign
        event.groupingHash = `useSubCollectionQuerySnapshot.${collection}.${subCollection}`;
        event.addMetadata('query', { collection, document: documentId, subCollection });
      });
    }
  }, [error, collection, documentId, subCollection]);

  return useMemo(() => {
    // Snapshot trigger with partial results and then with all results. This make the UI flicker.
    // According to the following post, once everything is received, metadata.fromCache is false.
    // To ensure this behaviour includeMetadataChanges is also set to true in the query.
    // https://github.com/firebase/firebase-js-sdk/issues/3053#issuecomment-628302623
    if (querySnapshot && !loading && !error && !querySnapshot?.metadata.fromCache) {
      return formatDocument<T>(querySnapshot);
    }

    return undefined;
  }, [querySnapshot, loading, error]);
};
